import { useEffect, useState } from "react";
import { Loader2, Plus, Pencil, Archive } from "lucide-react";
import { Button, Modal, Field, TextArea, Select, Switch } from "../../../shared/ui";
import {
  listAdminPlans,
  createAdminPlan,
  updateAdminPlan,
  deleteAdminPlan,
  type AdminPlan,
  type PlanInput,
} from "../../../features/admin/api/adminApi";
import { PageHeader, StatusPill, money } from "./_shared";
import { invalidatePlansCache } from "./WorkspaceBillingEditor";

const blank: PlanInput = {
  name: "",
  description: "",
  status: "draft",
  monthlyPrice: 0,
  annualPrice: 0,
  features: [],
  limits: { maxModels: 25, maxConfigurators: 5, maxDomains: 1, embedViews: 10000 },
  maxMembers: 1,
  storageEnabled: false,
  popular: false,
};

export default function AdminPlans() {
  const [plans, setPlans] = useState<AdminPlan[] | null>(null);
  const [editing, setEditing] = useState<AdminPlan | "new" | null>(null);

  const load = () => listAdminPlans().then(setPlans).catch(() => undefined);
  useEffect(() => { void load(); }, []);

  const archive = async (p: AdminPlan) => {
    if (!confirm(`Archive "${p.name}"? It will be hidden from the pricing page.`)) return;
    await deleteAdminPlan(p._id);
    invalidatePlansCache();
    await load();
  };

  return (
    <>
      <PageHeader
        title="Plans"
        subtitle="Subscription tiers + Stripe sync"
        actions={<Button variant="primary" size="sm" onClick={() => setEditing("new")}><Plus size={14} /> New plan</Button>}
      />
      <div className="p-4 sm:p-6">
        {!plans ? (
          <div className="flex h-40 items-center justify-center text-ink-400"><Loader2 className="animate-spin" /></div>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {plans.map((p) => (
              <div key={p._id} className={`surface-card p-5 ${p.status === "archived" ? "opacity-60" : ""}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-ink-900">{p.name}</h3>
                      {p.popular && <span className="rounded-full bg-gold-100 px-2 py-0.5 text-[10px] font-semibold text-gold-700">Popular</span>}
                    </div>
                    <p className="mt-0.5 text-[12px] text-ink-500">{p.description || "—"}</p>
                  </div>
                  <StatusPill status={p.status} />
                </div>
                <p className="mt-3 text-[22px] font-semibold text-ink-900">
                  {money(p.monthlyPrice)}<span className="text-[12px] font-normal text-ink-400">/mo</span>
                </p>
                <ul className="mt-3 space-y-1 text-[12px] text-ink-600">
                  <li>{p.limits.maxModels < 0 ? "Unlimited" : p.limits.maxModels} models</li>
                  <li>{p.limits.maxConfigurators < 0 ? "Unlimited" : p.limits.maxConfigurators} configurators · {p.limits.maxDomains < 0 ? "∞" : p.limits.maxDomains} domains</li>
                  <li>{p.maxMembers} member{p.maxMembers > 1 ? "s" : ""}{p.storageEnabled ? " · BYO-S3" : ""}</li>
                </ul>
                <div className="mt-4 flex items-center gap-2">
                  <Button variant="secondary" size="sm" onClick={() => setEditing(p)}><Pencil size={13} /> Edit</Button>
                  {p.status !== "archived" && (
                    <Button variant="ghost" size="sm" className="text-rose" onClick={() => archive(p)}><Archive size={13} /> Archive</Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <PlanModal
          plan={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); invalidatePlansCache(); await load(); }}
        />
      )}
    </>
  );
}

function PlanModal({ plan, onClose, onSaved }: { plan: AdminPlan | null; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState<PlanInput>(
    plan
      ? {
          name: plan.name, description: plan.description, status: plan.status,
          monthlyPrice: plan.monthlyPrice, annualPrice: plan.annualPrice, features: plan.features,
          limits: { ...plan.limits }, maxMembers: plan.maxMembers, storageEnabled: plan.storageEnabled, popular: plan.popular,
        }
      : { ...blank },
  );
  const [featuresText, setFeaturesText] = useState((plan?.features ?? []).join("\n"));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const set = (patch: Partial<PlanInput>) => setForm((f) => ({ ...f, ...patch }));
  const setLimit = (k: keyof NonNullable<PlanInput["limits"]>, v: number) =>
    setForm((f) => ({ ...f, limits: { ...(f.limits as NonNullable<PlanInput["limits"]>), [k]: v } }));

  const save = async () => {
    if (!form.name?.trim()) { setErr("Name is required"); return; }
    setSaving(true);
    setErr(null);
    const payload: PlanInput = { ...form, features: featuresText.split("\n").map((s) => s.trim()).filter(Boolean) };
    try {
      if (plan) await updateAdminPlan(plan._id, payload);
      else await createAdminPlan(payload);
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={plan ? `Edit ${plan.name}` : "New plan"}
      description="Pricing changes sync to Stripe automatically."
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="primary" size="sm" disabled={saving} onClick={save}>
            {saving ? <Loader2 size={13} className="animate-spin" /> : null} {plan ? "Save changes" : "Create plan"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {err && <p className="rounded-lg bg-rose/10 px-3 py-2 text-[12.5px] text-rose">{err}</p>}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Name" value={form.name ?? ""} onChange={(e) => set({ name: e.target.value })} />
          <Select label="Status" value={form.status ?? "draft"} onChange={(e) => set({ status: e.target.value as AdminPlan["status"] })}
            options={[{ value: "draft", label: "Draft" }, { value: "active", label: "Active" }, { value: "archived", label: "Archived" }]} />
        </div>
        <TextArea label="Description" value={form.description ?? ""} onChange={(e) => set({ description: e.target.value })} rows={2} />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <Field label="Monthly $" type="number" value={String(form.monthlyPrice ?? 0)} onChange={(e) => set({ monthlyPrice: Number(e.target.value) })} />
          <Field label="Annual $" type="number" value={String(form.annualPrice ?? 0)} onChange={(e) => set({ annualPrice: Number(e.target.value) })} />
          <Field label="Members" type="number" value={String(form.maxMembers ?? 1)} onChange={(e) => set({ maxMembers: Number(e.target.value) })} />
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Field label="Max models" type="number" value={String(form.limits?.maxModels ?? 0)} onChange={(e) => setLimit("maxModels", Number(e.target.value))} />
          <Field label="Configurators" type="number" value={String(form.limits?.maxConfigurators ?? 0)} onChange={(e) => setLimit("maxConfigurators", Number(e.target.value))} />
          <Field label="Domains" type="number" value={String(form.limits?.maxDomains ?? 0)} onChange={(e) => setLimit("maxDomains", Number(e.target.value))} />
          <Field label="Embed views" type="number" value={String(form.limits?.embedViews ?? 0)} onChange={(e) => setLimit("embedViews", Number(e.target.value))} />
        </div>
        <TextArea label="Features (one per line)" value={featuresText} onChange={(e) => setFeaturesText(e.target.value)} rows={4} />
        <div className="flex items-center gap-6">
          <Switch label="BYO-S3 storage" checked={!!form.storageEnabled} onChange={(e) => set({ storageEnabled: e.target.checked })} />
          <Switch label="Popular" checked={!!form.popular} onChange={(e) => set({ popular: e.target.checked })} />
        </div>
        <p className="text-[11px] text-ink-400">Tip: use -1 for unlimited limits.</p>
      </div>
    </Modal>
  );
}
